import { InnspelApiError, type InnspelStorage, type MySubmission, type SubmissionStatusView } from '@innspel/core';
import { useEffect, useId, useState } from 'react';
import { Ikon } from '../Ikon.tsx';
import { iGrunnlag, notiserAv } from './grunnlag.ts';
import { Banner, Btn, Card, H, Hint, P, Skeleton, StatusPill } from './primitiver.tsx';
import { markerSvarSett, merkNyeSvar, type MedNyhet } from './nyttSvar.ts';
import { Sporingslinje, sporingsledd } from './Sporingslinje.tsx';
import { saksnummerAv } from './saksnummer.ts';
import { formaterFrist, formaterTidspunkt, radEtikett, statusVisning, venterVisning } from './status.ts';
import { tekst } from './tekster.ts';

/**
 * 2j — «Mine innspill» (my submissions, DD-27). The offline queue is visible (DD-41): the row
 * that is waiting sits at the top, without a case number. The reply sits under the case, verbatim
 * as in the panel. The same component in the dialog and in the portal's full screen — that is
 * what the snapshot test proves.
 *
 * States: loading (skeleton in the shape of the rows) · empty (invitation, DD-19) ·
 * rows · error (handled by the caller, with the text kept).
 *
 * `onNytt` is optional: in the dialog the button sits in the footer (C2), while the portal's
 * full screen has it below the list.
 *
 * **«Legg til mer»** (add more, DD-69, tech-decisions «Tillegg fra innsenderen»): an open case
 * gets a text button under the promise, only when the server says `canAddAddendum`. A tap opens
 * one field INSIDE the card — no new page, no modal, the submitter is in the middle of something
 * else. The addenda sit under the submitter's own text, BEFORE the reply, with a neutral border:
 * they are the submitter's words, not a thread. After three the button is gone and the sentence
 * says what applies. The text is kept on error. Same words and same rules as in
 * `@innspel/react-native`.
 *
 * **H3.1 (concept page v0.1 2026-10-29, part A):** two things from the hypothesis module,
 * without a new status on the case (DD-56). While `inTestEvidence` is true, one line with an
 * icon sits between the reply and the progress line: «Innspillet ditt er med i grunnlaget for
 * noe vi tester.» — and never anything about the hypothesis (Kh6). Each `conclusionNotices[]`
 * sits under the reply as a RAISED surface (`surface-raised`, its own label «Hva vi fant ut ·
 * date») — another form than the reply's border, not just another colour (DD-08) — also on
 * cases that already have a reply or are rejected: a notice is not a reply (DD-27). «Nytt» on
 * a notice reuses the F14 mechanism (P4).
 *
 * **Offline (concept screen 3):** the host passes `sistKjentAt` when the rows are the list
 * from the previous fetch in this session. Then one sentence with the time stands in the info
 * colour, and the progress line, the promise and «Legg til mer» are gone — a deadline that
 * may have moved is not promised from an old list (DD-28). The list lives in memory, never in
 * storage (DD-62: «the core stores no list» is rejected; the queue is the only thing on disk).
 */
export function MySubmissions({
  rader,
  laster,
  tenantName,
  onNytt,
  overskriftNivaa = 2,
  storage,
  leggTil,
  onOppdatert,
  minTextLength,
  textMaxLength,
  onFeltApent,
  sistKjentAt,
}: {
  rader: MySubmission[];
  laster?: boolean;
  tenantName?: string;
  onNytt?: () => void;
  overskriftNivaa?: 1 | 2;
  /**
   * H3.1: the ISO time the list was last fetched, when `rader` is the old list shown
   * offline. Set = a banner with the time, no progress line, no promise, no action.
   */
  sistKjentAt?: string;
  /** The host's storage — «nytt svar» (new reply) is per device, never something the platform knows (F14). */
  storage: InnspelStorage;
  /** DD-69: the core's `subjects.addAddendum`. Omitted = the button does not exist (DD-26). */
  leggTil?: (submissionId: string, text: string) => Promise<SubmissionStatusView>;
  /** The updated case from the server after an addendum — swapped into `rader` by the host. */
  onOppdatert?: (sak: SubmissionStatusView) => void;
  /** The tenant's limits, as at submission (DD-44). */
  minTextLength?: number;
  textMaxLength?: number;
  /**
   * DD-69 clarified (F32): «én primærhandling om gangen» (one primary action at a time). The
   * dialog has «Send nytt innspill» in the footer, outside this component — it needs to know
   * when the addendum field is open, so the button can go. In full screen the component hides
   * its own button itself.
   */
  onFeltApent?: (apent: boolean) => void;
}) {
  const min = minTextLength ?? 10;
  const maks = textMaxLength ?? 280;
  const feltIdBase = useId();

  // DD-69: ett felt om gangen, i kortet det gjelder. Teksten beholdes til den er sendt
  // eller innsenderen avbryter — aldri mistet av en feil.
  const [apen, setApen] = useState<string | null>(null);
  const [utkast, setUtkast] = useState('');
  const [sender, setSender] = useState(false);
  const [feil, setFeil] = useState<{ id: string; tekst: string } | undefined>();
  const [lagtTil, setLagtTil] = useState<string | undefined>();

  useEffect(() => {
    onFeltApent?.(apen !== null);
  }, [apen, onFeltApent]);

  const send = async (id: string) => {
    if (!leggTil) return;
    setSender(true);
    setFeil(undefined);
    try {
      const sak = await leggTil(id, utkast.trim());
      onOppdatert?.(sak);
      setApen(null);
      setUtkast('');
      setLagtTil(id);
    } catch (e) {
      // 409 er ikke en feil i innsenderens tekst, men regelen sagt av serveren (race):
      // samme setning som når knappen er borte. Alt annet: årsaken, og teksten står.
      const kode = e instanceof InnspelApiError ? e.code : undefined;
      const tekstFeil =
        kode === 'addendum_limit_reached' ? tekst.tilleggTak
        : kode === 'submission_closed' ? tekst.tilleggLukket
        : tekst.tilleggFeil(e instanceof InnspelApiError && e.message ? e.message : tekst.tjenerenSvarteIkke);
      setFeil({ id, tekst: tekstFeil });
    } finally {
      setSender(false);
    }
  };
  // F14: nye svar først, og merket. «Nytt» er «kommet siden forrige gang denne enheten
  // viste lista». Samme regel som i @innspel/react-native. Feiler lagringen, vises lista
  // umerket i serverens rekkefølge.
  const umerket = (r: MySubmission[]): MedNyhet[] => r.map((rad) => ({ rad, nytt: false, nyttSvar: false, nyNotis: false }));
  const [visning, setVisning] = useState<MedNyhet[]>(() => umerket(rader));
  useEffect(() => {
    let gjelder = true;
    void merkNyeSvar(rader, storage)
      .then((m) => {
        if (gjelder) setVisning(m);
      })
      .catch(() => {
        if (gjelder) setVisning(umerket(rader));
      });
    return () => {
      gjelder = false;
    };
  }, [storage, rader]);

  // Lista er vist. Ved neste åpning er disse svarene ikke nye lenger — ikke når den hentes
  // i bakgrunnen for tellingen på knappen (DD-62), for da har hun ikke sett noe.
  useEffect(() => {
    if (!laster && rader.length) void markerSvarSett(storage).catch(() => undefined);
  }, [storage, laster, rader]);

  const gammel = sistKjentAt !== undefined;

  return (
    <div className="innspel-stabel">
      <H sub={tekst.mineUnder} nivaa={overskriftNivaa}>
        {tekst.mineTittel}
      </H>

      {laster ? (
        <div className="innspel-stabel" data-gap="sm" role="status" aria-label={tekst.mineLaster}>
          <Skeleton hoy />
          <Skeleton hoy />
          <Skeleton hoy />
        </div>
      ) : rader.length === 0 ? (
        <Card dashed testId="mine-tom">
          <span className="innspel-kvittering-ikon" data-tone="neutral">
            <Ikon navn="mail-outline" />
          </span>
          <P>{tekst.mineTom}</P>
          {/* Tomtilstanden er en invitasjon (DD-19), ikke meta — den leses i `body`. */}
          {tenantName ? <p className="innspel-hjelp">{tekst.mineTomHint(tenantName)}</p> : null}
        </Card>
      ) : (
        <>
        {/* H3.1, skjerm 3: offline er en delvis tilstand, ikke en feil — lista står, med tidspunktet. */}
        {gammel ? (
          <Banner ikon="cloud-offline-outline" tone="info" testId="mine-sist-kjent">
            {tekst.mineSistKjent(formaterTidspunkt(sistKjentAt))}
          </Banner>
        ) : null}
        <ul className="innspel-liste" data-testid="mine-liste">
          {visning.map(({ rad, nytt, nyttSvar }) => {
            const venter = rad.pending === true;
            const v = venter ? venterVisning : statusVisning(rad.status, rad.type, rad.deadlineAt);
            const nokkel = venter ? rad.clientRef : rad.id;
            const svar = venter ? undefined : rad.reply;
            const spor = rad.type === 'onske' ? tekst.onske : tekst.feil;
            const sakId = venter ? undefined : rad.id;
            const tillegg = venter ? [] : (rad.addenda ?? []);
            const notiser = notiserAv(rad);
            const kanLeggeTil = !venter && !gammel && !!leggTil && rad.canAddAddendum === true;
            const taketNådd = !venter && !gammel && rad.canAddAddendum === false && tillegg.length >= 3;
            const ledd = venter ? [] : sporingsledd(rad, tenantName);
            // F14 + H3.1 (P4): «Nytt svar» om svaret, bare «Nytt» om notisen — en notis er ikke et svar (DD-27).
            const merke = nytt ? (nyttSvar ? tekst.nyttSvar : tekst.nytt) : undefined;
            // Raden leses opp med det som er nytt, notisene og grunnlagslinjen — alt som står på skjermen.
            const etikett = venter
              ? `${tekst.mineIkkeSendt}: ${rad.text}. ${v.lofte}`
              : `${merke ? `${merke}. ` : ''}${radEtikett(rad)}${notiser.map((n) => ` ${tekst.hvaViFantUt(formaterFrist(n.at))}: ${n.text}`).join('')}${iGrunnlag(rad) ? ` ${tekst.grunnlagLinje}` : ''}`;
            const feltApent = sakId !== undefined && apen === sakId;
            const feltId = `${feltIdBase}-${sakId ?? ''}`;
            const brukt = utkast.length;
            const forKort = brukt < min;
            const full = brukt >= maks;
            const visTeller = brukt >= maks * 0.8;
            return (
              <li key={nokkel} aria-label={etikett}>
                <Card dashed={venter}>
                  <Hint>
                    {venter ? `${tekst.mineIkkeSendt} · ${spor}` : tekst.sakHode(spor, saksnummerAv(rad))}
                    {merke ? (
                      <span className="innspel-nytt-svar" data-testid="nytt-svar">
                        {merke}
                      </span>
                    ) : null}
                  </Hint>
                  <P>{rad.text}</P>
                  {tillegg.length > 0 ? (
                    <div className="innspel-tillegg-liste" data-testid={`tillegg-${sakId}`}>
                      {tillegg.map((a) => (
                        <div key={a.id} className="innspel-tillegg">
                          <span className="innspel-tillegg-fra">
                            <Ikon navn="add-outline" /> {tekst.duLaTil(formaterFrist(a.createdAt))}
                          </span>
                          <p className="innspel-tillegg-tekst">{a.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {/*
                    Rekkefølgen i kortet (DD-69 presisert 2026-09-20): innsenderens egen tekst →
                    tilleggene hennes → SVARET → sporingslinjen → løftet. Tillegget er hennes ord
                    og hører til teksten; svaret er det ferskeste som har skjedd (F14), og derfor
                    står det over linjen som sier hvor saken er.
                  */}
                  {svar ? (
                    <div className="innspel-svar">
                      <span className="innspel-svar-fra">
                        <Ikon navn="chatbubble-outline" />{' '}
                        {tenantName ? tekst.svarFra(tenantName) : tekst.svarFraGenerisk}
                      </span>
                      <p className="innspel-svar-tekst">{svar.text}</p>
                    </div>
                  ) : null}
                  {/*
                    H3.1: «Hva vi fant ut» — en HEVET flate ved siden av svaret, aldri et andre
                    svar (DD-27). Formen skiller den fra svarets kant (DD-08). Eldst først, som
                    kontrakten leverer. Bare id (nøkkel), tekst og dato — ingenting om hypotesen (Kh6).
                  */}
                  {notiser.map((n) => (
                    <div key={n.id} className="innspel-notis" data-testid={`notis-${n.id}`}>
                      <span className="innspel-notis-fra">
                        <Ikon navn="volume-medium-outline" />
                        {tekst.hvaViFantUt(formaterFrist(n.at))}
                      </span>
                      <p className="innspel-notis-tekst">{n.text}</p>
                    </div>
                  ))}
                  {/* H3.1: én linje mens saken er i grunnlaget — ikon og setning, ingen boks, ikke en status (DD-56). */}
                  {iGrunnlag(rad) ? (
                    <p className="innspel-grunnlag" data-testid={`grunnlag-${sakId}`}>
                      <Ikon navn="flask-outline" />
                      <span>{tekst.grunnlagLinje}</span>
                    </p>
                  ) : null}
                  {/*
                    DD-08 presisert 2026-09-20: statusen har TO former, og begge bærer ikon og
                    etikett. En åpen sak får sporingslinjen — nå-leddet ER statusen, og datoen
                    står som tall når kontrakten har den. En avsluttet sak får pillen og én
                    linje: det er ikke noe igjen å spore. Aldri begge.

                    Fra en gammel liste (H3.1, uten nett) loves ingen frist og ingen status
                    (DD-28) — bare det som alt var hentet.
                  */}
                  {gammel ? null : ledd.length > 0 ? (
                    <Sporingslinje ledd={ledd} testId={`spor-${sakId}`} />
                  ) : (
                    <div className="innspel-status">
                      <StatusPill ikon={v.ikon} etikett={v.etikett} fg={v.fg} bg={v.bg} />
                    </div>
                  )}
                  {gammel ? null : <p className="innspel-lofte">{v.lofte}</p>}
                  {!venter && rad.attachmentRemoved ? (
                    <div className="innspel-rad" data-testid={`bilde-fjernet-${rad.id}`}>
                      <Ikon navn="trash-outline" />
                      <Hint>{tekst.bildeFjernet}</Hint>
                    </div>
                  ) : null}
                  {sakId !== undefined && lagtTil === sakId ? (
                    <p className="innspel-lagt-til" role="status" aria-live="polite" data-testid={`lagt-til-${sakId}`}>
                      <Ikon navn="checkmark-circle-outline" /> {tekst.lagtTil}
                    </p>
                  ) : null}
                  {feltApent ? (
                    <div className="innspel-felt" data-testid={`tillegg-felt-${sakId}`}>
                      <label className="innspel-tillegg-etikett" htmlFor={feltId}>
                        {tekst.leggTilMerLabel}
                      </label>
                      <textarea
                        id={feltId}
                        className="innspel-tekstfelt"
                        data-testid="tillegg-tekst"
                        data-full={full ? 'true' : undefined}
                        value={utkast}
                        onChange={(e) => setUtkast(e.target.value)}
                        maxLength={maks}
                        rows={3}
                        autoFocus
                        placeholder={tekst.leggTilMerPlaceholder}
                        aria-describedby={feil?.id === sakId ? `${feltId}-feil` : undefined}
                      />
                      {visTeller ? (
                        <span className="innspel-teller" data-testid="tillegg-teller" data-full={full ? 'true' : undefined} role="status" aria-live="polite">
                          {full ? <Ikon navn="warning-outline" /> : null}
                          {brukt} / {maks}
                          {full ? tekst.tellerFull : ''}
                        </span>
                      ) : null}
                      {feil?.id === sakId ? (
                        <p className="innspel-feilmelding" id={`${feltId}-feil`} role="alert" data-testid={`tillegg-feil-${sakId}`}>
                          {feil.tekst}
                        </p>
                      ) : null}
                      <div className="innspel-rad">
                        <Btn
                          testId="tillegg-send"
                          label={sender ? tekst.sender : tekst.send}
                          disabled={forKort || sender}
                          busy={sender}
                          {...(forKort ? { hint: tekst.forKort(min) } : {})}
                          onClick={() => sakId !== undefined && void send(sakId)}
                          grow
                        />
                        <Btn
                          testId="tillegg-avbryt"
                          label={tekst.avbryt}
                          variant="secondary"
                          onClick={() => {
                            setApen(null);
                            setUtkast('');
                            setFeil(undefined);
                          }}
                          grow
                        />
                      </div>
                      <Hint>{tekst.leggTilMerHint}</Hint>
                    </div>
                  ) : kanLeggeTil ? (
                    <button
                      type="button"
                      className="innspel-legg-til-mer"
                      data-testid={`legg-til-mer-${sakId}`}
                      aria-label={tekst.leggTilMerLabel}
                      onClick={() => {
                        if (sakId === undefined) return;
                        setApen(sakId);
                        setUtkast('');
                        setFeil(undefined);
                        setLagtTil(undefined);
                      }}
                    >
                      <Ikon navn="add-outline" /> {tekst.leggTilMer}
                    </button>
                  ) : taketNådd ? (
                    <Hint>{tekst.tilleggTak}</Hint>
                  ) : null}
                </Card>
              </li>
            );
          })}
        </ul>
        </>
      )}

      {/* F32 (DD-69 presisert): én primærhandling om gangen — knappen er borte mens et tillegg skrives. */}
      {/* H3.1, skjerm 3: uten nett er knappen omriss — det som skrives, lagres og sendes når nettet er tilbake (DD-41). */}
      {onNytt && apen === null ? (
        <Btn testId="send-nytt" ikon="chatbubble-outline" label={rader.length === 0 ? tekst.sendForste : tekst.sendNytt} onClick={onNytt} variant={gammel ? 'secondary' : 'primary'} />
      ) : null}
    </div>
  );
}
